"use client";

import React from "react";
import { Phone, Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function QuoteAction() {
  const shouldReduceMotion = useReducedMotion();
  const directContacts = [{
    title: "Call our team",
    value: "01344 929900",
    href: "tel:01344929900",
    icon: Phone,
    description: "Direct line for site visits and immediate scaffolding advice",
    type: "tel"
  }, {
    title: "Email drawings & specs",
    value: "quotes@bmfscaffolding.co.uk",
    href: "mailto:quotes@bmfscaffolding.co.uk",
    icon: Mail,
    description: "Send project drawings, schedules, or scopes of work",
    type: "mailto"
  }];
  return <section id="finalcalltoaction" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28">
      {/* Subtle background decoration */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,_var(--color-primary)/0.06,_transparent_70%)]" />

      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
          {/* Main CTA Block */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary">
              <ShieldCheck className="size-3.5 text-primary" />
              <span>NASC & CHAS Accredited Contractor</span>
            </div>

            <h2 className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
              Ready to plan your access requirements?
            </h2>

            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Speak directly with experienced scaffolding managers covering Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East. We provide clear, itemised quotations tailored to your site specifications.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 h-12 shadow-sm">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <div className="text-xs text-muted-foreground sm:pl-2">
                Fast turnarounds on tender submissions & site visits
              </div>
            </div>
          </motion.div>

          {/* Contact Direct Cards */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            {directContacts.map((contact, i) => {
            const IconComp = contact.icon;
            return <motion.div key={contact.value} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.3
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: "easeOut"
            }}>
                  <Card className="border border-border/70 bg-card shadow-sm hover:border-primary/50 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                          <IconComp className="size-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {contact.title}
                          </span>
                          <div className="mt-1">
                            {contact.type === "tel" ? <a href="tel:01344929900" className="text-lg font-bold text-foreground hover:text-primary transition-colors break-words">
                                {contact.value}
                              </a> : <a href="mailto:quotes@bmfscaffolding.co.uk" className="text-lg font-bold text-foreground hover:text-primary transition-colors break-words">
                                {contact.value}
                              </a>}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {contact.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}